// src/components/MentorshipRequestModal.tsx
import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Avatar,
  Alert,
  CircularProgress,
  useTheme,
  alpha
} from '@mui/material';
import { School as MentorIcon } from '@mui/icons-material';
import * as mentorshipApi from '../services/mentorship';

interface MentorshipRequestModalProps {
  open: boolean;
  onClose: () => void;
  expert: {
    id: string;
    firstName: string;
    lastName: string;
    title?: string;
    organizationName?: string;
    photoUrl?: string;
  };
  memberName: string;
  memberCompany?: string;
  memberTitle?: string;
  onSuccess?: () => void;
}

const MentorshipRequestModal: React.FC<MentorshipRequestModalProps> = ({
  open,
  onClose,
  expert,
  memberName,
  memberCompany,
  memberTitle,
  onSuccess
}) => {
  const theme = useTheme();
  const [requestMessage, setRequestMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async () => {
    if (!requestMessage.trim()) {
      setError('Please describe what you want from this mentorship');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      await mentorshipApi.createMentorshipRequest(expert.id, requestMessage.trim());

      setSuccess(true);
      setTimeout(() => {
        onSuccess?.();
        handleClose();
      }, 2000);
    } catch (err: any) {
      console.error('Error creating mentorship request:', err);
      const errorMessage = err?.response?.data?.error?.message || 'Failed to send mentorship request. Please try again.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setRequestMessage('');
      setError(null);
      setSuccess(false);
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.95)}, ${alpha(theme.palette.background.paper, 1)})`,
        }
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: '50%',
              background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`
            }}
          >
            <MentorIcon sx={{ color: 'white', fontSize: 28 }} />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Request Mentorship
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Connect with {expert.firstName} {expert.lastName}
            </Typography>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent>
        {success ? (
          <Alert severity="success" sx={{ mb: 2 }}>
            Mentorship request sent successfully! The expert will review your request.
          </Alert>
        ) : (
          <>
            {/* Expert Info */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                p: 2,
                mb: 3,
                borderRadius: 2,
                bgcolor: alpha(theme.palette.primary.main, 0.05),
                border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`
              }}
            >
              <Avatar
                src={expert.photoUrl || ''}
                alt={`${expert.firstName} ${expert.lastName}`}
                sx={{ width: 56, height: 56 }}
              />
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  {expert.firstName} {expert.lastName}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {expert.title}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {expert.organizationName}
                </Typography>
              </Box>
            </Box>

            {/* Auto-filled Member Info */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                Your Information
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <TextField
                  size="small"
                  label="Name"
                  value={memberName}
                  disabled
                  fullWidth
                />
                <TextField
                  size="small"
                  label="Title"
                  value={memberTitle || 'Not specified'}
                  disabled
                  fullWidth
                />
                <TextField
                  size="small"
                  label="Company"
                  value={memberCompany || 'Not specified'}
                  disabled
                  fullWidth
                />
              </Box>
            </Box>

            {/* Request Message */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                What do you want from this mentorship? *
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={4}
                placeholder="Describe your goals, what you're looking to learn, or specific areas where you need guidance..."
                value={requestMessage}
                onChange={(e) => setRequestMessage(e.target.value)}
                error={!!error && !requestMessage.trim()}
                disabled={loading}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    '&.Mui-focused': {
                      boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.1)}`
                    }
                  }
                }}
              />
            </Box>

            {/* Free Plan Info */}
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>Free Plan:</strong> 3 mentors per month, 50 messages per mentor
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                Upgrade to Pro for 10 mentors and 500 messages per mentor (50 SAR/month)
              </Typography>
            </Alert>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        {!success && (
          <>
            <Button onClick={handleClose} disabled={loading}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={loading || !requestMessage.trim()}
              sx={{
                background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                minWidth: 120
              }}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : 'Send Request'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default MentorshipRequestModal;
